import React from "react"
import { observer } from "mobx-react-lite"
import { useAlert } from "react-alert"

import BaseModal from "@components/modals/components/BaseModal/BaseModal"
import useModal from "@components/modals/useModal"
import Text from "@components/ui/Typography/Text"
import { SimpleUserData } from "@framework/types/user"
import Avatar from "@components/ui/Avatar/Avatar"
import { capitalizeFirstLetter } from "@utils/textUtils"
import Markdown from "@components/ui/Typography/Markdown"
import Button from "@components/ui/Button/Button"
import Icon from "@components/ui/Icon/Icon"
import Templates from "@components/ui/Templates"

import { ModalsTypes } from "../constants"

import styles from "./QueryAnswerModal.module.sass"

export type QueryAnswerModalProps = {
  answerSummary: string
  question: string
  user: SimpleUserData
}

const QueryAnswerModal: React.FC<QueryAnswerModalProps> = observer(
  ({ answerSummary, question, user }) => {
    const alert = useAlert()
    const modal = useModal(ModalsTypes.QUERY_ANSWER_MODAL)

    const handleCopyClick = () => {
      const textToCopy = answerSummary || ""
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          alert.success("Text copied successfully")
        })
        .catch(() => {
          alert.error("Failed to copy text")
        })
    }

    return (
      <BaseModal onClose={modal.hideModal} className={styles.root}>
        <Templates.RollScript
          className={styles.body}
          gutter="16"
          headerSocket={
            <Text
              variant="body1"
              weight="bold"
              align="center"
              className={styles.header}
            >
              <Avatar
                className={styles.avatar}
                name={user.name}
                src={user.avatarUrl}
                height={24}
                width={24}
              />
              <Text variant="h5">{capitalizeFirstLetter(question)}</Text>
            </Text>
          }
          footerSocket={
            <Button
              size="small"
              variant="contained"
              color="secondary"
              before={<Icon name="checkbox-multiple-blank" />}
              onClick={handleCopyClick}
            >
              Copy
            </Button>
          }
        >
          <div className={styles.answerBox}>
            <div className={styles.answer}>
              <Markdown>{answerSummary}</Markdown>
            </div>
          </div>
        </Templates.RollScript>
      </BaseModal>
    )
  }
)

export default QueryAnswerModal
